import { EditOutlined, EyeInvisibleOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { Sharing } from '@common/entities';
import { MPButton, MPInput, MPPassword, MPTable } from '@components/index';
import { DatePicker, Form, Space, type TableProps } from 'antd';
type ColumnsType<T = any> = NonNullable<TableProps<T>['columns']>;
import dayjs, { Dayjs } from 'dayjs';
import _range from 'lodash/range';
import { useState } from 'react';

export default function SharingTable({ value, onChange }: { value?: Sharing[]; onChange?: (value: Sharing[]) => void }) {
    const onFinish = (values: Sharing) => {
        onChange?.([...(value || []), values]);
    };

    const handleDelete = (id: string) => {
        onChange?.([...(value || [])].filter(item => item.id !== id));
    };

    const onChangeField = (id: string, field: string, newValue: string | Dayjs) => {
        onChange?.([...(value || [])].map(item => (item.id === id ? { ...item, [field]: newValue } : item)));
    };

    const columns: ColumnsType<Sharing> = [
        {
            title: 'Name',
            dataIndex: 'name',
        },
        {
            title: 'Password',
            dataIndex: 'password',
            render: (value, record) => {
                const onChange = (value: string) => {
                    onChangeField(record.id, 'password', value);
                };

                return (
                    <PasswordField
                        value={value}
                        onChange={onChange}
                    />
                );
            },
        },
        {
            title: 'Valid Until',
            dataIndex: 'validUntil',
            render: (value, record) => {
                const onChange = (value: Dayjs) => {
                    onChangeField(record.id, 'validUntil', value);
                };

                return (
                    <ValidUntilField
                        value={value}
                        onChange={onChange}
                    />
                );
            },
        },
        {
            title: 'Actions',
            dataIndex: 'id',
            render: (value: string) => (
                <MPButton
                    type="link"
                    onClick={() => handleDelete(value)}
                >
                    Delete
                </MPButton>
            ),
        },
    ];

    return (
        <>
            <SharingForm onFinish={onFinish} />

            <Form.Item
                name={['accessManagement', 'sharing']}
                valuePropName="dataSource"
            >
                <MPTable
                    bordered={true}
                    columns={columns}
                    pagination={{ hideOnSinglePage: true }}
                    rowKey="id"
                />
            </Form.Item>
        </>
    );
}

function PasswordField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
    const [editMode, setEditMode] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const [visible, setVisible] = useState(false);

    if (editMode) {
        return (
            <Space.Compact>
                <MPPassword
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                />
                <MPButton
                    type="link"
                    onClick={() => {
                        setEditValue(value);
                        setEditMode(false);
                    }}
                >
                    Cancel
                </MPButton>
                <MPButton
                    type="primary"
                    onClick={() => {
                        onChange(editValue);
                        setEditMode(false);
                    }}
                >
                    Save
                </MPButton>
            </Space.Compact>
        );
    }
    return (
        <div className="flex items-center">
            <span>{visible ? value : '*'.repeat(value.length)}</span>
            <MPButton
                type="link"
                onClick={() => setVisible(!visible)}
            >
                {visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            </MPButton>
            <MPButton
                type="link"
                onClick={() => setEditMode(true)}
            >
                <EditOutlined />
            </MPButton>
        </div>
    );
}

function ValidUntilField({ value, onChange }: { value: Dayjs; onChange: (value: Dayjs) => void }) {
    const [editMode, setEditMode] = useState(false);
    const [editValue, setEditValue] = useState(value);
    if (editMode) {
        return (
            <Space.Compact>
                <DatePicker
                    showTime={{ format: 'HH:mm' }}
                    disabledDate={currentDate => currentDate.isBefore(dayjs(), 'day')}
                    disabledTime={currentDate => ({
                        disabledHours: () => (currentDate.isSame(dayjs(), 'day') ? _range(0, dayjs().hour() + 1) : []),
                    })}
                    format="MM-DD-YYYY HH:mm"
                    value={editValue}
                    onChange={(date) => date && setEditValue(date)}
                />
                <MPButton
                    type="link"
                    onClick={() => {
                        setEditValue(value);
                        setEditMode(false);
                    }}
                >
                    Cancel
                </MPButton>
                <MPButton
                    type="primary"
                    onClick={() => {
                        onChange(editValue);
                        setEditMode(false);
                    }}
                >
                    Save
                </MPButton>
            </Space.Compact>
        );
    }

    return (
        <div className="flex items-center gap-2">
            <span>{!value ? 'Unlimited' : dayjs(value).format('MM/DD/YYYY HH:mm')}</span>
            <MPButton
                type="link"
                onClick={() => setEditMode(true)}
            >
                <EditOutlined />
            </MPButton>
        </div>
    );
}

function SharingForm({ onFinish }: { onFinish: (values: Sharing) => void }) {
    const [form] = Form.useForm<Sharing>();
    const sharing = Form.useWatch<Sharing[]>(['accessManagement', 'sharing']);

    function _onFinish(values: Sharing) {
        values.id = crypto.randomUUID();
        onFinish(values);
        form.resetFields();
    }

    return (
        <Form
            form={form}
            onFinish={_onFinish}
            className="mb-6!"
        >
            <Space.Compact>
                <Form.Item
                    name="name"
                    rules={[
                        {
                            required: true,
                            message: 'Sharing Name is required',
                        },
                        {
                            validator: (_, value) => {
                                if (sharing?.find(item => item.name === value)) {
                                    return Promise.reject(new Error('Sharing Name already exists'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <MPInput placeholder="Sharing Name" />
                </Form.Item>
                <Form.Item
                    name="password"
                    rules={[
                        { required: true, message: 'Sharing Password is required' },
                        {
                            validator: (_, value) => {
                                if (sharing?.find(item => item.password === value)) {
                                    return Promise.reject(new Error('You cannot use the same password for multiple sharings'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <MPPassword placeholder="Sharing Password" />
                </Form.Item>
                <Form.Item name="validUntil">
                    <DatePicker
                        showTime={{ format: 'HH:mm' }}
                        format="MM-DD-YYYY HH:mm"
                        placeholder="Sharing Valid Until"
                        className="w-00"
                        disabledDate={currentDate => currentDate.isBefore(dayjs(), 'day')}
                        disabledTime={currentDate => ({
                            disabledHours: () => (currentDate.isSame(dayjs(), 'day') ? _range(0, dayjs().hour() + 1) : []),
                        })}
                    />
                </Form.Item>
                <MPButton
                    type="primary"
                    onClick={() => form.submit()}
                    icon={<PlusOutlined />}
                >
                    Add Sharing
                </MPButton>
            </Space.Compact>
        </Form>
    );
}
